import { useRef } from 'react';
import type { AsesoriaPlan } from '../../../../hooks/useSupabaseAdmin';

interface PlanFormProps {
  editing: AsesoriaPlan | null;
  onCancel: () => void;
  onSave: (formData: FormData) => void;
  formError: string | null;
  saving?: boolean;
}

export default function PlanForm({
  editing,
  onCancel,
  onSave,
  formError,
  saving = false,
}: PlanFormProps) {
  const formRef = useRef<HTMLFormElement>(null);

  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault();
        onSave(new FormData(e.currentTarget));
      }}
      className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-5"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-brand-black text-lg">
          {editing ? 'Editar Plan' : 'Nuevo Plan'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
        >
          <i className="ri-close-line text-xl" />
        </button>
      </div>

      {formError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 mt-0.5" />
          <p className="font-sans text-red-600 text-sm">{formError}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
            Nombre *
          </label>
          <input
            name="name"
            defaultValue={editing?.name ?? ''}
            required
            disabled={saving}
            className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black disabled:opacity-50"
          />
        </div>
        <div>
          <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
            Precio *
          </label>
          <input
            name="price"
            defaultValue={editing?.price ?? ''}
            required
            disabled={saving}
            className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black disabled:opacity-50"
          />
        </div>
        <div>
          <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
            Período *
          </label>
          <input
            name="period"
            defaultValue={editing?.period ?? ''}
            required
            disabled={saving}
            className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black disabled:opacity-50"
          />
        </div>
      </div>

      <div>
        <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
          Descripción
        </label>
        <textarea
          name="description"
          defaultValue={editing?.description ?? ''}
          rows={3}
          disabled={saving}
          className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none disabled:opacity-50"
        />
      </div>

      <div>
        <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
          Características (una por línea)
        </label>
        <textarea
          name="features"
          defaultValue={editing?.features?.join('\n') ?? ''}
          rows={6}
          disabled={saving}
          className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none disabled:opacity-50"
        />
      </div>

      <div>
        <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
          Texto del botón
        </label>
        <input
          name="cta"
          defaultValue={editing?.cta ?? 'Solicitar'}
          disabled={saving}
          className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black disabled:opacity-50"
        />
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="highlighted"
          name="highlighted"
          defaultChecked={editing?.highlighted ?? false}
          disabled={saving}
          className="w-4 h-4 rounded border-brand-gray-300 text-brand-black focus:ring-brand-black disabled:opacity-50"
        />
        <label
          htmlFor="highlighted"
          className="font-sans text-brand-gray-600 text-sm"
        >
          Marcar como plan destacado
        </label>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
        >
          {saving && <i className="ri-loader-4-line animate-spin" />}
          {saving ? 'Guardando...' : 'Guardar Plan'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap disabled:opacity-50"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
